import type { IncomeSplitRule } from '@fruitful/income-split';
import type { DepositAccount, ImAccount } from '@fruitful/types';
import { router } from 'expo-router';
import { useAtomValue } from 'jotai';
import * as React from 'react';
import {
    BodyMediumRegular,
    ChevronDownIcon,
    FruitfulCustomIcon,
    Icon,
    InstitutionLogo,
    LabelRegular,
    XStack,
    YStack,
    type YStackProps,
} from 'smoothie';

import { incomeSplitRuleEditorModeAtom } from '../../../state/PaycheckRoutingAtoms';
import type { BankAccountDisplayDetail } from '../../../types/BankAccount';
import PaycheckRoutingCard from '../../paycheck-routing/common/PaycheckRoutingCard';

interface RuleTargetAccountProps {
    bankAccounts: BankAccountDisplayDetail[];
    externalAccounts: DepositAccount[];
    internalInvestmentAccounts?: ImAccount[];
    rule: Partial<IncomeSplitRule>;
    cardProps?: YStackProps;
}

const RuleEditorAccount = ({
    bankAccounts,
    externalAccounts,
    internalInvestmentAccounts,
    rule,
}: RuleTargetAccountProps) => {
    const incomeSplitRuleEditorMode = useAtomValue(
        incomeSplitRuleEditorModeAtom,
    );

    const isAddingNewRule = incomeSplitRuleEditorMode === 'Add';

    const targetAccount =
        bankAccounts.find(
            (details) => details.bankAccountId === rule.accountId,
        ) ??
        externalAccounts.find((account) => account.id === rule.accountId) ??
        internalInvestmentAccounts?.find(
            (account) => account.apexAccountId === rule.accountId,
        );

    const isExternal =
        targetAccount != null &&
        'isExternal' in targetAccount &&
        targetAccount?.isExternal;
    const isInternalInvestment = isExternal
        ? false
        : targetAccount != null &&
          (targetAccount as ImAccount).apexAccountId != null;
    const { color, icon, nickname } = targetAccount ?? {};

    const renderAccountCard = () => (
        <YStack gap={8}>
            <LabelRegular marginLeft={8} color="$gray1100">
                Destination account
            </LabelRegular>
            <PaycheckRoutingCard
                onPress={() => {
                    isAddingNewRule &&
                        router.navigate(
                            '/income-split/edit/account?tiedRules=true',
                        );
                }}
            >
                <XStack justifyContent="space-between" alignItems="center">
                    <XStack gap={12} alignItems="center">
                        {targetAccount && (
                            <>
                                {isExternal ? (
                                    <InstitutionLogo
                                        size="xxs"
                                        logo={
                                            targetAccount.logo ??
                                            targetAccount.imgBase64
                                        }
                                    />
                                ) : (
                                    <FruitfulCustomIcon
                                        size="xxs"
                                        icon={icon}
                                        color={color}
                                        useAltColors={
                                            isInternalInvestment ?? false
                                        }
                                    />
                                )}
                            </>
                        )}
                        <BodyMediumRegular
                            color={targetAccount ? '$black' : '$gray800'}
                        >
                            {isExternal
                                ? targetAccount.accountType
                                : (nickname ?? 'Choose account')}
                        </BodyMediumRegular>
                    </XStack>
                    {isAddingNewRule && (
                        <Icon icon={ChevronDownIcon} color="$gray1000" />
                    )}
                </XStack>
            </PaycheckRoutingCard>
        </YStack>
    );

    return <YStack gap={8}>{renderAccountCard()}</YStack>;
};

export default RuleEditorAccount;
